import { useQueryClient } from '@tanstack/react-query';
import { FC, useRef, useState } from 'react';
import CanvasDraw from 'react-canvas-draw';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import { ReactComponent as UndoIcon } from '@/assets/icons/undo.svg';
import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Textarea } from '@/components/Forms';
import { Modal } from '@/components/Modal';
import { SidePanelFooter } from '@/components/SidePanel';
import Text from '@/components/Text';
import useToasts from '@/hooks/useToasts';
import { MediaResource } from '@/types/resources';
import { api } from '@/utils/api';
import { CoverImagesImageStatus } from '@/utils/enums';

const colorButtons = [
  { color: '#E20000' },
  { color: '#111111' },
  { color: '#00E232' },
  { color: '#EEEEEE' },
];

const Dialog = styled(StyledDialog)`
  width: 1000px;
  padding: 0;
`;

const ModalContent = styled.div`
  display: flex;
`;

const ContentLeft = styled.div`
  width: 61.6%;
  padding: 38px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px, 0px, 0px, 4px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

const ContentRight = styled.div`
  width: 38.4%;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-radius: 4px;
  padding: 24px;
  position: relative;
  box-shadow: 0px 2px 8px rgba(34, 34, 34, 0.06);
`;

const TextAreaHolder = styled.div`
  padding: 24px 32px;
  margin-bottom: 40px;
`;

const StyledTextarea = styled(Textarea)`
  min-height: 310px;
  margin-bottom: 18px;
`;

const CanvasButtons = styled.div`
  margin-top: 32px;
  gap: 32px;
  display: flex;
  align-items: center;
`;

const CanvasButton = styled(Button)`
  height: 38px;
  background-color: transparent;
  font-size: 14px;
  position: relative;
  padding: 0 16px;
  svg {
    margin-right: 10px;
  }
  &:first-child {
    &:after {
      content: '';
      position: absolute;
      right: -17px;
      height: 100%;
      width: 1px;
      background-color: ${({ theme }) => theme.colors.lightGray};
    }
  }
`;

const EraseCanvasButton = styled(CanvasButton)`
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.error};
  }
`;

const ColorButtons = styled.div`
  display: flex;
  align-items: center;
  margin-left: -16px;
  gap: 8px;
`;

const ColorButton = styled.button<{ $active?: boolean }>`
  height: 38px;
  width: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: solid 1px ${({ theme }) => theme.colors.lightGray};
  border-radius: 6px;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  ${({ $active }) =>
    $active &&
    css`
      border-color: ${({ theme }) => theme.colors.gray};
    `}
`;

const ColorButtonInner = styled.div<{ $color: string }>`
  width: 26px;
  height: 26px;
  border-radius: 2px;
  background-color: ${({ $color }) => $color};
`;

interface Props {
  onClose: () => void;
  image: MediaResource<
    {
      preview?: string;
    },
    {
      status: CoverImagesImageStatus;
      isMain?: boolean;
      comment: string;
    }
  >;
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
}

const ImageCommentModal: FC<Props> = ({
  onClose,
  image,
  setSelectedImages,
}) => {
  const [keepOriginal, setKeepOriginal] = useState(false);

  const [isPosting, setIsPosting] = useState(false);

  const [brushColor, setBrushColor] = useState('#E20000');

  const { id } = useParams();

  const queryClient = useQueryClient();

  const toasts = useToasts();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const saveableCanvas = useRef<CanvasDraw | any>(null);

  const combineImagesOnCanvas = (
    originalImageSrc: string,
    base64ImageSrc: string,
  ) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      if (!canvas) return reject(new Error('Canvas not available'));

      const ctx = canvas.getContext('2d');
      const originalImage = new Image();
      const base64Image = new Image();

      originalImage.crossOrigin = 'anonymous';
      base64Image.crossOrigin = 'anonymous';

      if (!ctx) {
        return;
      }

      originalImage.onload = () => {
        canvas.width = 1200;
        canvas.height = 630;
        ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
        base64Image.onload = () => {
          ctx.drawImage(base64Image, 0, 0, 1200, 630);
          resolve(canvas.toDataURL('image/png'));
        };
        base64Image.onerror = reject;
        base64Image.src = base64ImageSrc;
      };
      originalImage.onerror = reject;
      originalImage.src = originalImageSrc;
    });
  };

  const onPostComment = async () => {
    const toastId = toasts.loading();
    setIsPosting(true);
    try {
      const drawingDataUrl = saveableCanvas.current.getDataURL();

      const drawingDataExists =
        JSON.parse(saveableCanvas.current.getSaveData()).lines.length > 0;

      let imageDataUrl;

      if (drawingDataExists) {
        imageDataUrl = await combineImagesOnCanvas(
          image.versions.original,
          drawingDataUrl,
        );
      }

      await api.post(`/v1/cover-images/image-comment/${id}`, {
        comment: textAreaRef.current?.value,
        ...(drawingDataExists && { image_data: imageDataUrl }),
        image_id: image.id,
        keep_original: keepOriginal,
      });

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      toasts.success(toastId);
      setSelectedImages([]);
      onClose();
    } catch (_) {
      toasts.error(toastId);
    }
    setIsPosting(false);
  };

  return (
    <Modal zIndex={1}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <ModalContent>
          <ContentLeft>
            <CanvasDraw
              ref={saveableCanvas}
              brushColor={brushColor}
              brushRadius={2}
              lazyRadius={2}
              canvasWidth={540}
              canvasHeight={283.5}
              imgSrc={image.versions.original}
              gridColor="#fff"
            />
            <CanvasButtons>
              <EraseCanvasButton
                color="secondary"
                small
                onClick={() => {
                  saveableCanvas.current?.eraseAll();
                }}
              >
                <TrashIcon />
                Erase
              </EraseCanvasButton>
              <CanvasButton
                color="secondary"
                small
                onClick={() => {
                  saveableCanvas.current?.undo();
                }}
              >
                <UndoIcon />
                Undo
              </CanvasButton>
              <ColorButtons>
                {colorButtons.map((button, index) => (
                  <ColorButton
                    key={index}
                    $active={button.color === brushColor}
                    onClick={() => setBrushColor(button.color)}
                  >
                    <ColorButtonInner $color={button.color} />
                  </ColorButton>
                ))}
              </ColorButtons>
            </CanvasButtons>
          </ContentLeft>
          <ContentRight>
            <ModalHeader>
              <Text $type="button">Leave a comment · Cover </Text>
              <CloseButton onClick={onClose}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13 13L1 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CloseButton>
            </ModalHeader>
            <TextAreaHolder>
              <StyledTextarea
                disabled={isPosting}
                ref={textAreaRef}
                placeholder="Add comment"
              ></StyledTextarea>
              <Checkbox
                checked={keepOriginal}
                onChange={() => setKeepOriginal((prev) => !prev)}
              >
                Keep original cover
              </Checkbox>
            </TextAreaHolder>
            <SidePanelFooter $actions>
              <Button
                type="button"
                disabled={isPosting}
                onClick={onPostComment}
              >
                Save
              </Button>
              <Button
                color="secondary"
                disabled={isPosting}
                onClick={onClose}
                type="button"
              >
                Cancel
              </Button>
            </SidePanelFooter>
          </ContentRight>
        </ModalContent>
      </Dialog>
    </Modal>
  );
};

export default ImageCommentModal;
